import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  MessageCircle,
  Send,
  Settings,
  ShieldCheck,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const StatPill = ({ children }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-700">
    <Check size={16} className="text-emerald-600" />
    <span className="font-medium">{children}</span>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center mb-4`}>
      <Icon size={22} />
    </div>
    <h3 className="text-xl font-bold text-[#141414] mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const WatokPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = useMemo(() => {
    const items = t('watok.features', { returnObjects: true });
    const config = [
      { icon: MessageCircle, colorClass: 'bg-emerald-50 text-emerald-600' },
      { icon: Send, colorClass: 'bg-blue-50 text-blue-600' },
      { icon: Users, colorClass: 'bg-purple-50 text-purple-600' },
      { icon: Settings, colorClass: 'bg-amber-50 text-amber-600' }
    ];
    return items.map((item, idx) => ({ ...item, ...config[idx] }));
  }, [t]);

  const operations = useMemo(() => t('watok.operations_list', { returnObjects: true }), [t]);
  const commitments = useMemo(() => t('watok.security_points', { returnObjects: true }), [t]);
  const securityStats = useMemo(() => t('watok.security_stats', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader hideCTA />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/apps'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <section className="grid grid-cols-1 lg:grid-cols-[1fr_0.95fr] gap-12 items-center mb-24">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-emerald-100">
                <ShieldCheck size={16} />
                {t('watok.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-6">
                {t('watok.title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mb-8 font-medium">
                {t('watok.subtitle')}
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                {t('watok.pills', { returnObjects: true }).map((pill) => (
                  <StatPill key={pill}>{pill}</StatPill>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#watok-features"
                  className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('watok.cta_features')}
                </a>
                <Link
                  to="/watok-security-policy"
                  className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('watok.cta_policy')}
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-3 text-sm text-gray-500">
                <ShieldCheck size={16} className="text-gray-400" />
                {t('watok.support_note')}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-emerald-500/10 via-teal-500/10 to-blue-500/10 blur-2xl rounded-3xl" />
              <div className="relative bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                <img
                  src="/images/watok/cover.png"
                  alt="WATOK preview"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/1200x750/f3f4f6/111827?text=WATOK';
                  }}
                />
              </div>
            </div>
          </section>

          <section id="watok-features" className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('watok.features_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('watok.features_subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
              {features.map((feature) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.desc}
                  colorClass={feature.colorClass}
                />
              ))}
            </div>
          </section>

          <section className="mb-24">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
                <div>
                  <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                    <Settings size={16} />
                    {t('watok.operations_badge')}
                  </div>
                  <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('watok.operations_title')}</h2>
                  <p className="text-gray-600 leading-relaxed">
                    {t('watok.operations_desc')}
                  </p>
                </div>

                <div className="space-y-4">
                  {operations.map((item) => (
                    <div key={item} className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5">
                      <Check size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-24">
            <div className="bg-[#141414] text-white rounded-3xl p-8 md:p-12 border border-white/10 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/30 via-slate-900/10 to-blue-900/30" />
              <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <ShieldCheck size={16} />
                    {t('watok.security_badge')}
                  </div>
                  <h2 className="text-3xl font-bold mb-4">{t('watok.security_title')}</h2>
                  <p className="text-white/80 leading-relaxed mb-6">
                    {t('watok.security_desc')}
                  </p>
                  <div className="space-y-3 mb-8">
                    {commitments.map((item) => (
                      <div key={item} className="flex items-start gap-3 text-white/85">
                        <Check size={18} className="text-emerald-300 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/watok-security-policy"
                    className="inline-flex bg-white/10 border border-white/15 text-white hover:bg-white/15 font-bold py-2.5 px-6 rounded-full transition"
                  >
                    {t('watok.security_link')}
                  </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {securityStats.map((item) => (
                    <div key={item.label} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <div className="text-sm text-white/70 font-semibold">{item.label}</div>
                      <div className="mt-2 text-lg font-bold">{item.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('watok.footer_title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              {t('watok.footer_desc')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#watok-features"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-10 rounded-full transition"
              >
                {t('watok.footer_primary')}
              </a>
              <Link
                to="/apps"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-10 rounded-full transition"
              >
                {t('watok.footer_secondary')}
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default WatokPage;
